/**
 * Dialogue UI — US 01.03/01.04, pure DOM. Lower-screen panel: speaker name,
 * text, up to 4 choice buttons or a single Continue. Greyed choices show the
 * authored reason. Escape or ✕ closes; walking out of range closes within
 * the story's 100ms — the server keeps the cursor either way, so closing is
 * always "pause", never "cancel". Movement is never locked.
 */
import { worldBus } from '@shared/bus.js';
import { newActionId } from '@shared/envelope.js';
import { callDialogue } from '@transport/api.js';
import { reportError } from '@transport/observability.js';
import { toast } from './design/components.js';
import { showOverflowPrompt } from './prompts.js';

interface ClientChoice {
  id: string;
  text: string;
  enabled: boolean;
  disabledReason?: string;
}
interface ClientNode {
  nodeId: string;
  npcId: string;
  text: string;
  choices: ClientChoice[];
  canContinue: boolean;
  isTerminal: boolean;
}
interface ChooseResult {
  node: ClientNode | null;
  effects: unknown[];
  state?: unknown;
  celebration?: { xpGained: number; levelledUp: boolean; duplicate: boolean; rarity?: string };
}

export function initDialoguePanel(
  root: HTMLElement,
  onStateChanged: () => void,
  itemName: (id: string) => string = (id) => id,
): void {
  const panel = document.createElement('div');
  panel.className = 'wca-dialogue hidden';
  root.appendChild(panel);

  let current: ClientNode | null = null;
  let pendingCount = 0;
  let busy = false;

  function close(): void {
    if (current && !current.isTerminal) {
      worldBus.emit('npc-paused', { npcId: current.npcId, paused: true });
    }
    current = null;
    panel.classList.add('hidden');
  }

  function render(node: ClientNode | null): void {
    if (!node) {
      close();
      return;
    }
    if (node.isTerminal && node.choices.length === 0 && !node.canContinue) {
      // Terminal narration: show it with a single close affordance.
      worldBus.emit('npc-paused', { npcId: node.npcId, paused: false });
    }
    current = node;
    panel.classList.remove('hidden');
    panel.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'wca-dialogue-head';
    const name = document.createElement('span');
    name.textContent = npcNames.get(node.npcId) ?? node.npcId;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.className = 'wca-close';
    closeBtn.addEventListener('click', close);
    head.append(name, closeBtn);

    const text = document.createElement('p');
    text.className = 'wca-dialogue-text';
    text.textContent = node.text;

    const choices = document.createElement('div');
    choices.className = 'wca-dialogue-choices';

    const addButton = (
      label: string,
      enabled: boolean,
      reason: string | undefined,
      pick: () => void,
    ) => {
      const b = document.createElement('button');
      b.className = 'wca-choice';
      b.textContent = label;
      b.disabled = !enabled;
      if (reason) {
        const r = document.createElement('small');
        r.textContent = ` — ${reason}`;
        b.appendChild(r);
      }
      b.addEventListener('click', pick);
      choices.appendChild(b);
    };

    if (node.canContinue) {
      addButton('Continue', true, undefined, () => void advance(undefined));
    } else if (node.isTerminal || node.choices.length === 0) {
      addButton('Close', true, undefined, () => {
        worldBus.emit('npc-paused', { npcId: node.npcId, paused: false });
        current = null;
        panel.classList.add('hidden');
      });
    } else {
      for (const c of node.choices) {
        addButton(c.text, c.enabled, c.disabledReason, () => void advance(c.id));
      }
    }

    // A bundle left behind earlier waits at this NPC (US 01.06).
    if (pendingCount > 0) {
      addButton(`Collect waiting items (${pendingCount})`, true, undefined, () => {
        void callDialogue<{ collected: { qty: number }[]; remaining: number }>({
          op: 'collect_pending',
          npcId: node.npcId,
        }).then((r) => {
          if (!r.ok) return;
          pendingCount = r.result.remaining;
          onStateChanged();
          if (current) render(current);
        });
      });
    }

    panel.append(head, text, choices);
  }

  /**
   * Feedback moments (US 17.05, minimal until the design system lands):
   * picture acquisition gets a celebratory toast; everything else waits for
   * the real treatment pass in P1.
   */
  function announceEffects(effects: unknown[], rarity?: string): void {
    const kinds = effects
      .filter((e): e is { kind: string } => typeof e === 'object' && e !== null && 'kind' in e)
      .map((e) => e.kind);
    if (kinds.includes('picture')) {
      // Rarity scales the treatment (US 17.05): rare and above celebrate.
      const big = rarity && !['common', 'uncommon', 'very_uncommon'].includes(rarity);
      toast(
        root,
        `📸 ${big ? `${rarity.replace(/_/g, ' ').toUpperCase()} picture` : 'Picture'} acquired!`,
        big ? 'celebrate' : 'info',
      );
    }
    // Area unlock (US 03.01): the lock disappears on the next sync — say why.
    if (kinds.includes('area')) toast(root, '🔓 A new area has opened up!');
  }

  async function advance(choiceId: string | undefined): Promise<void> {
    if (!current || busy) return;
    busy = true;
    try {
      const npcId = current.npcId;
      const actionId = newActionId();
      let res = await callDialogue<ChooseResult>(
        { op: 'choose', npcId, ...(choiceId ? { choiceId } : {}) },
        { actionId },
      );
      // Overflow (US 01.06): show the honest prompt, then re-send the SAME
      // actionId with the player's decision. Dismissal cancels the trade.
      if (!res.ok && res.error === 'INVENTORY_FULL' && res.manifest && choiceId) {
        const mode = await showOverflowPrompt(root, res.manifest, itemName);
        if (!mode) return;
        res = await callDialogue<ChooseResult>(
          { op: 'choose', npcId, choiceId, confirmOverflow: mode },
          { actionId },
        );
      }
      if (!res.ok) {
        // A refused choice re-renders current state; the server said why.
        if (res.error === 'REQUIREMENTS_NOT_MET' || res.error === 'INVENTORY_FULL') {
          await open(npcId); // re-resolve: enabled flags may differ
        }
        return;
      }
      if (res.result.effects.length > 0) {
        onStateChanged();
        announceEffects(res.result.effects, res.result.celebration?.rarity);
      }
      const cel = res.result.celebration;
      if (cel?.levelledUp) toast(root, '🎉 Level up!', 'celebrate');
      if (res.result.node) render(res.result.node);
      else {
        if (current) worldBus.emit('npc-paused', { npcId: current.npcId, paused: false });
        current = null;
        panel.classList.add('hidden');
      }
    } catch (err) {
      reportError(err, { msg: 'dialogue advance' });
    } finally {
      busy = false;
    }
  }

  async function open(npcId: string): Promise<void> {
    const res = await callDialogue<{ node: ClientNode; pendingCount?: number }>({
      op: 'interact',
      npcId,
    });
    if (res.ok) {
      pendingCount = res.result.pendingCount ?? 0;
      render(res.result.node);
    }
  }

  const npcNames = new Map<string, string>();
  void fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/npc_public?select=id,name`, {
    headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '' },
  })
    .then((r) => r.json())
    .then((rows: { id: string; name: string }[]) => {
      for (const row of rows) npcNames.set(row.id, row.name);
    })
    .catch(() => undefined); // names fall back to ids offline

  worldBus.on('interact-requested', ({ npcId }) => void open(npcId));
  worldBus.on('interaction-changed', ({ npcId }) => {
    // Leaving range closes within 100ms (US 01.04) — the tick emits this
    // immediately, so the close is effectively same-frame.
    if (npcId === null && current) close();
  });
  worldBus.on('ui-intent', ({ intent }) => {
    if (intent === 'CANCEL' && current) close();
  });
}
